(() => {
  if (window.opProductCardInteraction) return;
  window.opProductCardInteraction = true;

  const cardSelector = "[data-op-product-card]";
  const mediaSelector = "[data-op-media-link]";
  const activeSelector = `${cardSelector}.is-secondary`;
  const secondarySelector = ".op-product-card__image--secondary";
  const preparedImages = new WeakMap();

  let activeCard = null;
  let requestedCard = null;
  let touchActive = false;
  let touchPoint = null;
  let touchFrame = 0;

  const resetCards = (exceptCard = null) => {
    document.querySelectorAll(activeSelector).forEach((card) => {
      if (card !== exceptCard) card.classList.remove("is-secondary");
    });

    if (activeCard !== exceptCard) activeCard = null;
  };

  const markReady = async (card, image) => {
    if (typeof image.decode === "function") {
      try {
        await image.decode();
      } catch (_) {
        // A loaded image can still reject decode in older Safari versions.
      }
    }

    if (!image.naturalWidth) return false;
    card.classList.add("is-secondary-ready");
    return true;
  };

  const prepareCard = (card) => {
    if (!card?.classList.contains("has-secondary")) return Promise.resolve(false);

    const image = card.querySelector(secondarySelector);
    if (!image) return Promise.resolve(false);
    if (preparedImages.has(image)) return preparedImages.get(image);

    if (image.dataset.srcset) {
      image.srcset = image.dataset.srcset;
      delete image.dataset.srcset;
    }
    if (image.dataset.src) {
      image.src = image.dataset.src;
      delete image.dataset.src;
    }
    image.loading = "eager";
    image.fetchPriority = "low";

    const loaded = image.complete && image.naturalWidth
      ? Promise.resolve()
      : new Promise((resolve) => {
          image.addEventListener("load", resolve, { once: true });
          image.addEventListener("error", resolve, { once: true });
        });

    const ready = loaded.then(() => markReady(card, image));
    preparedImages.set(image, ready);
    return ready;
  };

  const commitCard = (card) => {
    if (requestedCard !== card) return;
    resetCards(card);
    card.classList.add("is-secondary");
    activeCard = card;
  };

  const activateCard = (card) => {
    if (!card || !card.classList.contains("has-secondary")) {
      requestedCard = null;
      resetCards();
      return;
    }

    if (requestedCard === card && activeCard === card) return;
    requestedCard = card;
    resetCards(card);

    if (card.classList.contains("is-secondary-ready")) {
      commitCard(card);
      return;
    }

    prepareCard(card).then((ready) => {
      if (ready) commitCard(card);
    });
  };

  const deactivateCard = (card) => {
    if (!card) return;
    if (requestedCard === card) requestedCard = null;
    if (activeCard === card) activeCard = null;
    card.classList.remove("is-secondary");
  };

  const cardAtPoint = (x, y) => {
    const target = document.elementFromPoint(x, y);
    const media = target?.closest(mediaSelector);
    return media?.closest(cardSelector) || null;
  };

  const updateTouchTarget = () => {
    touchFrame = 0;
    if (!touchActive || !touchPoint) return;

    const card = cardAtPoint(touchPoint.x, touchPoint.y);
    if (card) activateCard(card);
  };

  const queueTouchUpdate = (touch) => {
    if (!touch) return;
    touchPoint = { x: touch.clientX, y: touch.clientY };
    if (!touchFrame) touchFrame = window.requestAnimationFrame(updateTouchTarget);
  };

  document.addEventListener("pointerover", (event) => {
    if (event.pointerType === "touch") return;

    const card = event.target.closest(cardSelector);
    if (!card || card.contains(event.relatedTarget)) return;
    activateCard(card);
  });

  document.addEventListener("pointerout", (event) => {
    if (event.pointerType === "touch") return;

    const card = event.target.closest(cardSelector);
    if (!card || card.contains(event.relatedTarget)) return;
    deactivateCard(card);
  });

  document.addEventListener(
    "touchstart",
    (event) => {
      if (event.touches.length !== 1) return;
      touchActive = true;
      queueTouchUpdate(event.touches[0]);
    },
    { passive: true },
  );

  document.addEventListener(
    "touchmove",
    (event) => {
      if (!touchActive || event.touches.length !== 1) return;
      queueTouchUpdate(event.touches[0]);
    },
    { passive: true },
  );

  window.addEventListener(
    "scroll",
    () => {
      if (touchActive && touchPoint && !touchFrame) {
        touchFrame = window.requestAnimationFrame(updateTouchTarget);
      }
    },
    { passive: true },
  );

  const finishTouch = (event) => {
    if (event.touches?.length) return;
    touchActive = false;
    touchPoint = null;
    if (touchFrame) window.cancelAnimationFrame(touchFrame);
    touchFrame = 0;
  };

  document.addEventListener("touchend", finishTouch, { passive: true });
  document.addEventListener("touchcancel", finishTouch, { passive: true });

  const observeCards = (root = document) => {
    const cards = [];
    if (root.matches?.(cardSelector)) cards.push(root);
    root.querySelectorAll?.(cardSelector).forEach((card) => cards.push(card));
    cards.forEach((card) => preloadObserver?.observe(card));
  };

  const preloadObserver = "IntersectionObserver" in window
    ? new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            entry.target.classList.toggle("is-nearby", entry.isIntersecting);
            if (entry.isIntersecting) prepareCard(entry.target);
          });
        },
        { rootMargin: "250px 0px", threshold: 0.01 },
      )
    : null;

  if (preloadObserver) {
    observeCards();

    if ("MutationObserver" in window) {
      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === 1) observeCards(node);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  window.addEventListener(
    "pageshow",
    () => {
      requestedCard = null;
      resetCards();
    },
    { passive: true },
  );

  document.addEventListener("shopify:section:unload", () => {
    requestedCard = null;
    resetCards();
  });
})();
